import React, { useState, useEffect } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    IconButton,
    useMediaQuery,
    useTheme,
    Button,
    Snackbar,
    Alert,
    Stack,
    Menu,
    MenuItem,
    Fade,
    Typography,
    Divider,
    Avatar,
    ListItemIcon,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BugReportIcon from '@mui/icons-material/BugReport';
import HomeIcon from '@mui/icons-material/Home';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import DnsIcon from '@mui/icons-material/Dns';
import SecurityIcon from '@mui/icons-material/Security'; // Replace SecurityScan with Security
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Sidebar from './Sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useAuth } from '../../hooks/useAuth';
import scanApi from '../../services/api';  // Aggiungi questo import se non presente
import AnimatedLogo from '../Logo/AnimatedLogo';

const MainLayout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const navigate = useNavigate();
    const { mode } = useCustomTheme();
    const { user } = useAuth();

    const [exploits, setExploits] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [notification, setNotification] = useState(null);

    const [toolsAnchorEl, setToolsAnchorEl] = useState(null);
    const openTools = Boolean(toolsAnchorEl);

    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);

    // Add connection status states
    const [connectionStatus, setConnectionStatus] = useState('connected');
    const [connectionError, setConnectionError] = useState(null);

    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const handleToolsClick = (event) => {
        setToolsAnchorEl(event.currentTarget);
    };

    const handleToolsClose = () => {
        setToolsAnchorEl(null);
    };

    const handleUserMenuOpen = (event) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleLogout = () => {
        handleUserMenuClose();
        try {
            scanApi.logout();
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
        } catch (error) {
            console.error('Logout failed:', error);
            // Forza comunque il logout
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
        }
    };

    const navigateExternal = (url) => {
        window.location.href = url;
    };

    const handleNotificationBellClick = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = () => {
        setNotificationsAnchorEl(null);
    };

    const renderNavigation = () => (
        <Stack 
            direction="row" 
            spacing={1}
            alignItems="center"
            sx={{ 
                display: { xs: 'none', md: 'flex' },
                // Rimuovi il margine sinistro dal container principale
                transition: theme => theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                '& .MuiButton-root': {
                    color: 'text.primary',
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    '&:hover': {
                        bgcolor: 'action.hover',
                    }
                }
            }}
        >
            <Box sx={{ 
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
            }}>
                <AnimatedLogo isOpen={isOpen} />
            </Box>

            {/* Sposta i pulsanti di navigazione dopo il logo */}
            <Stack 
                direction="row" 
                spacing={1}
                sx={{ ml: isOpen ? '200px' : '48px' }} // Aggiungi margine dopo il logo
            >
                <Button
                    startIcon={<HomeIcon />}
                    onClick={() => navigateExternal('https://exploitfinder.com')}
                >
                    Home
                </Button>

                <Button
                    startIcon={<BugReportIcon />}
                    onClick={() => navigateExternal('https://exploitfinder.com/dbexploit/#exploits')}
                >
                    Exploits
                </Button>

                <Button
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={handleToolsClick}
                    startIcon={<BuildIcon />}
                    sx={{
                        bgcolor: openTools ? 'action.hover' : 'transparent'
                    }}
                >
                    Tools
                </Button>

                <Menu
                    anchorEl={toolsAnchorEl}
                    open={openTools}
                    onClose={handleToolsClose}
                    TransitionComponent={Fade}
                    sx={{
                        '& .MuiPaper-root': {
                            width: 220,
                            mt: 1,
                            backgroundImage: 'none'
                        }
                    }}
                >
                    <MenuItem 
                        onClick={() => {
                            navigateExternal('https://exploitfinder.com/app/subfinder.php');
                            handleToolsClose();
                        }}
                        sx={{ gap: 1.5 }}
                    >
                        <DnsIcon fontSize="small" />
                        <Typography variant="body2">Subdomain Finder</Typography>
                    </MenuItem>
                    
                    <MenuItem 
                        onClick={() => {
                            navigateExternal('https://scanner.exploitfinder.com/');
                            handleToolsClose();
                        }}
                        sx={{ gap: 1.5 }}
                    >
                        <SecurityIcon fontSize="small" /> {/* Changed from SecurityScanIcon to SecurityIcon */}
                        <Typography variant="body2">Website Scanner</Typography>
                    </MenuItem>
                    
                    <MenuItem 
                        onClick={() => {
                            navigateExternal('https://exploitfinder.com/app/portscanner.php');
                            handleToolsClose();
                        }}
                        sx={{ gap: 1.5 }}
                    >
                        <SearchIcon fontSize="small" />
                        <Typography variant="body2">Port Scanner</Typography>
                    </MenuItem>
                    
                    <MenuItem 
                        onClick={() => {
                            navigateExternal('https://exploitfinder.com/app/urlfuzzer.php');
                            handleToolsClose();
                        }}
                        sx={{ gap: 1.5 }}
                    >
                        <ManageSearchIcon fontSize="small" />
                        <Typography variant="body2">URL Fuzzer</Typography>
                    </MenuItem>
                </Menu>
            </Stack>
        </Stack>
    );

    const renderUserInfo = () => {
        if (!user) return null;

        // Aggiungi la definizione di initials
        const initials = user.name
            ?.split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase() || '?';

        return (
            <Stack direction="row" spacing={2} alignItems="center">
                {/* Notification Bell - ridimensionata */}
                <IconButton
                    onClick={handleNotificationBellClick}
                    size="small"  // Ridotta dimensione del bottone
                    sx={{ 
                        color: 'text.primary',
                        position: 'relative',
                        padding: '4px'  // Ridotto padding
                    }}
                >
                    <Badge 
                        badgeContent={unreadCount} 
                        color="error"
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: '#EF4444',
                                color: 'white',
                                fontSize: '0.65rem',  // Ridotta dimensione del testo
                                height: '16px',       // Ridotta altezza del badge
                                minWidth: '16px',     // Ridotta larghezza minima
                            }
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: '1.25rem' }} />  {/* Ridotta dimensione icona */}
                    </Badge>
                </IconButton>

                {/* Notifications Popover */}
                <Popover
                    open={Boolean(notificationsAnchorEl)}
                    anchorEl={notificationsAnchorEl}
                    onClose={handleNotificationClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            width: 320,
                            maxHeight: 400,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                            border: '1px solid',
                            borderColor: 'divider',
                        }
                    }}
                >
                    {renderNotifications()}
                </Popover>

                <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center"
                    onClick={handleUserMenuOpen}
                    sx={{ cursor: 'pointer' }}
                >
                    <Avatar sx={{ 
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40,
                        fontSize: '1.2rem'
                    }}>
                        {initials}
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ display: { xs: 'none', md: 'block' } }}>
                        {user.name}
                    </Typography>
                </Stack>

                <Menu
                    anchorEl={userMenuAnchorEl}
                    open={Boolean(userMenuAnchorEl)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem onClick={() => {
                        handleUserMenuClose();
                        navigate('/profile');
                    }}>
                        <ListItemIcon>
                            <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography>Profile</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography>Sign out</Typography>
                    </MenuItem>
                </Menu>
            </Stack>
        );
    };

    // Load sidebar state from localStorage
    useEffect(() => {
        const savedState = localStorage.getItem('sidebarOpen');
        if (savedState !== null) {
            setIsOpen(JSON.parse(savedState));
        }
    }, []);

    // Save sidebar state to localStorage
    const handleToggle = () => {
        setIsOpen(prev => {
            const newState = !prev;
            localStorage.setItem('sidebarOpen', JSON.stringify(newState));
            return newState;
        });
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleScanNow = () => {
        const token = localStorage.getItem('token');
        if (token && user) {
            navigate('/scan');
        } else {
            navigate('/login');
        }
    };

    const handleConnectionError = (error) => {
        setConnectionStatus('error');
        setConnectionError(error.message || 'Connection error');
    };

    const resetConnection = () => {
        setConnectionStatus('connected');
        setConnectionError(null);
    };

    useEffect(() => {
        const fetchExploits = async () => {
            try {
                const response = await fetch('https://opendb.exploitfinder.com:5555/api/last-stats');
                const data = await response.json();
                if (data && Array.isArray(data.items)) {
                    setExploits(data.items);
                    // Aggiorna le notifiche con i nuovi dettagli
                    const latestNotifications = data.items.slice(0, 5).map(exploit => ({
                        id: exploit.id,
                        message: exploit.description || 'New vulnerability detected',
                        timestamp: exploit.timeStamp,
                        read: false,
                        link: exploit.referenceURL || `https://exploitfinder.com/dbexploit/#exploit-${exploit.id}`,
                        type: exploit.type,
                        source: exploit.source,
                        cve: exploit.id.startsWith('CVE-') ? exploit.id : null
                    }));
                    
                    // Aggiorna le notifiche mantenendo il conteggio corretto
                    setNotifications(prevNotifications => {
                        const newNotifications = [...latestNotifications];
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                        
                        // Filtra le vecchie notifiche
                        const filteredOldNotifications = prevNotifications.filter(n => 
                            new Date(n.timestamp) > sevenDaysAgo &&
                            !newNotifications.some(newN => newN.id === n.id)
                        );
                        
                        // Combina e ordina
                        const combined = [...newNotifications, ...filteredOldNotifications];
                        return combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                    });

                    // Aggiorna il conteggio delle non lette correttamente
                    setUnreadCount(prevCount => {
                        const existingIds = new Set(notifications.map(n => n.id));
                        const newUnreadCount = latestNotifications.filter(n => 
                            !existingIds.has(n.id) && !n.read
                        ).length;
                        return newUnreadCount;
                    });
                }
            } catch (error) {
                console.error('Failed to fetch exploits:', error);
            }
        };
        fetchExploits();

        // Imposta un intervallo per il fetch periodico
        const intervalId = setInterval(fetchExploits, 300000); // ogni 5 minuti
        return () => clearInterval(intervalId);
    }, []);

    // Aggiungi una funzione per gestire il click sulla notifica
    const handleNotificationClick = (notification) => {
        // Marca la notifica come letta
        setNotifications(prevNotifications =>
            prevNotifications.map(n =>
                n.id === notification.id ? { ...n, read: true } : n
            )
        );
        
        // Decrementa il contatore delle non lette
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        
        // Se c'è un link, naviga
        if (notification.link) {
            window.open(notification.link, '_blank');
        }
        
        handleNotificationClose();
    };

    // Modifica il rendering delle notifiche nel Popover
    const renderNotifications = () => (
        <List sx={{ p: 0 }}>
            {notifications.length > 0 ? (
                notifications.map((notification) => (
                    <ListItem 
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: notification.read ? 'transparent' : 'action.hover',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'action.selected'
                            },
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            py: 1.5
                        }}
                    >
                        {notification.cve && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#EF4444',
                                    fontWeight: 600,
                                    display: 'block'
                                }}
                            >
                                {notification.cve}
                            </Typography>
                        )}
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'text.primary',
                                fontWeight: notification.read ? 400 : 600
                            }}
                        >
                            {notification.message}
                        </Typography>
                        <Stack 
                            direction="row" 
                            spacing={2} 
                            alignItems="center"
                            sx={{ width: '100%', mt: 0.5 }}
                        >
                            <Typography 
                                variant="caption" 
                                sx={{ color: 'text.secondary' }}
                            >
                                {new Date(notification.timestamp).toLocaleString()}
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#818CF8',
                                    ml: 'auto',
                                    cursor: 'pointer',
                                    '&:hover': { color: '#6366F1' }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(notification.link, '_blank');
                                }}
                            >
                                View Details →
                            </Typography>
                        </Stack>
                    </ListItem>
                ))
            ) : (
                <ListItem>
                    <ListItemText
                        primary="No notifications"
                        primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            align: 'center'
                        }}
                    />
                </ListItem>
            )}
        </List>
    );

    const renderContent = () => {
        if (connectionStatus === 'initializing') {
            return (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '200px' 
                }}>
                    <Typography>Initializing connection...</Typography>
                </Box>
            );
        }

        if (connectionError) {
            return (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '200px' 
                }}>
                    <Typography color="error" gutterBottom>{connectionError}</Typography>
                    <Button variant="contained" onClick={resetConnection}>
                        Retry Connection
                    </Button>
                </Box>
            );
        }

        // Render the main content (children) directly
        return children;
    };

    return (
        <Box sx={{ display: 'flex', backgroundColor: 'background.default', minHeight: '100vh' }}>
            <AppBar 
                position="fixed" 
                sx={{ 
                    backgroundColor: 'background.sidebar',
                    borderBottom: 1,
                    borderColor: 'divider',
                    width: '100%',
                    transition: 'all 0.3s ease',
                    zIndex: (theme) => theme.zIndex.drawer + 1, // Assicura che l'AppBar sia sopra il drawer
                    height: 64 // Fixed height for the AppBar
                }}
                elevation={0}
            >
                <Toolbar 
                    sx={{ 
                        justifyContent: 'space-between',
                        height: '100%',
                        pl: { sm: `${isOpen ? 240 : 73}px` },
                        transition: theme => theme.transitions.create('padding', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        })
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton
                            color="inherit"
                            onClick={handleDrawerToggle}
                            edge="start"
                            size="large"
                            sx={{
                                transition: 'transform 0.3s ease',
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                                display: { sm: 'none' }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        {renderNavigation()}
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RocketLaunchIcon />}
                            onClick={handleScanNow}
                            sx={{
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                color: 'white',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #1976D2 30%, #00B4D8 90%)',
                                    transform: 'scale(1.05)',
                                    transition: 'transform 0.2s'
                                }
                            }}
                        >
                            {user ? 'Scan Now' : 'Start Scanning'}
                        </Button>
                        {renderUserInfo()}
                    </Stack>
                </Toolbar>
            </AppBar>

            <Sidebar 
                mobileOpen={mobileOpen}
                onDrawerToggle={handleDrawerToggle}
                onNavigate={handleNavigation}
                currentPath={location.pathname}
                isMobile={isMobile}
                isOpen={isOpen}
                onToggle={handleToggle}
            />

            <Box 
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    pt: 10,
                    width: { sm: `calc(100% - ${isOpen ? 240 : 73}px)` },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    })
                }}
            >
                {renderContent()}
            </Box>
            <Snackbar
                open={openSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {notification && (
                    <Alert severity="info">
                        <strong>New Exploit Alert:</strong> {notification.description}
                    </Alert>
                )}
            </Snackbar>
        </Box>
    );
};

export default MainLayout;